/**
 * op-object.js — one picked object: its soft mask, its lit level, the colour
 * under the tap («only the main colour») — and the painter that gives it a
 * new colour. CPU, no DOM, UMD (page, worker, node).
 *
 * Every constant here was chosen by the lab of 21.10.2026 on real photos
 * (LoC Kodachromes, Prokudin-Gorsky, Commons product photos — see
 * docs/why/primitives/object-paint.md):
 *
 *   · the mask is feathered INWARD only: feathering outward moved the 3-px
 *     band outside the object by ΔE00 5.9–7.6, inward kept it at exactly 0;
 *   · the lit level is the 70th percentile of a self-guided-filtered
 *     luminance inside the object (r = 8 px at 1280, ε = 4e-4);
 *   · «only the main colour» clusters the object's pixels on shading-
 *     invariant features (OKLab a/L, b/L and the lightness against its
 *     surroundings), k = 4, merges clusters closer than 0.12 (one material in
 *     light and shade stays one) and keeps the cluster under the tap. It kept
 *     the wheels and windows of a car, the stripes of a marinière, the logo of
 *     a printed tee (0.9–8 % leakage, 99.7–100 % of the tapped colour covered).
 *   · a chromatic material takes its own REFLECTION (29.10.2026): a cluster
 *     whose linear-light chroma vector points the material's way (≤ 10°) is
 *     the same paint with sky in it — the sky-lit half of a glossy car stayed
 *     in the old colour without it; a pale print of the same hue (17–25° off)
 *     stays out.
 *
 * Preview and export call the same paint(); the analysis is made once at the
 * working size and sampled bilinearly at any other size.
 *
 * Public API — window.OpObject / module.exports:
 *   analyze({rgba, w, h, mask, tap}) → Analysis
 *   referenceOf({rgba, w, h, mask})  → {rgb, lut, yMed} | null
 *   prepare(an, colour)              → painter params for paint()
 *   paint(src, dst, w, h, layers)    layers: [{an, P, keepPattern, strength}]
 */
(function (root, factory) {
    var api = factory(root.OpColor || (typeof require === 'function' ? require('./op-color.js') : null),
                      root.MaskEdge || (typeof require === 'function' ? require('../mask-edge.js') : null));
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    else root.OpObject = api;
}(typeof self !== 'undefined' ? self : this, function (C, ME) {
    'use strict';
    var LIN = C.LIN;

    /* ── box / guided filters (O(N) per pass, separable running sums) ─────── */
    function boxBlur(src, w, h, r, out) {
        var tmp = new Float32Array(w * h);
        out = out || new Float32Array(w * h);
        for (var y = 0; y < h; y++) {
            var row = y * w, acc = 0, n = 0;
            for (var x = -r; x <= r; x++) { if (x >= 0 && x < w) { acc += src[row + x]; n++; } }
            for (var x2 = 0; x2 < w; x2++) {
                tmp[row + x2] = acc / n;
                var xo = x2 - r, xi = x2 + r + 1;
                if (xo >= 0) { acc -= src[row + xo]; n--; }
                if (xi < w) { acc += src[row + xi]; n++; }
            }
        }
        // vertical pass row by row with per-column running sums: walking a
        // column at a time strides the whole plane and missed the cache (1.4 s
        // of a 2.5 MP analysis, profiled 21.10.2026; this form is ~20× faster)
        var col = new Float64Array(w), n2 = 0;
        for (var yy = 0; yy <= r && yy < h; yy++) { var ro = yy * w; for (var c0 = 0; c0 < w; c0++) col[c0] += tmp[ro + c0]; n2++; }
        for (var y2 = 0; y2 < h; y2++) {
            var inv = 1 / n2, base = y2 * w;
            for (var c1 = 0; c1 < w; c1++) out[base + c1] = col[c1] * inv;
            var yo = y2 - r, yi = y2 + r + 1;
            if (yo >= 0) { var ob = yo * w; for (var c2 = 0; c2 < w; c2++) col[c2] -= tmp[ob + c2]; n2--; }
            if (yi < h) { var ib = yi * w; for (var c3 = 0; c3 < w; c3++) col[c3] += tmp[ib + c3]; n2++; }
        }
        return out;
    }
    // He et al. guided filter: guide I, input p.
    function guided(I, p, w, h, r, eps) {
        var n = w * h, Ip = new Float32Array(n), II = new Float32Array(n);
        for (var i = 0; i < n; i++) { Ip[i] = I[i] * p[i]; II[i] = I[i] * I[i]; }
        var mI = boxBlur(I, w, h, r), mp = boxBlur(p, w, h, r), mIp = boxBlur(Ip, w, h, r), mII = boxBlur(II, w, h, r);
        var a = Ip, b = II;          // reuse
        for (var j = 0; j < n; j++) {
            var cov = mIp[j] - mI[j] * mp[j], v = mII[j] - mI[j] * mI[j];
            a[j] = cov / (v + eps); b[j] = mp[j] - a[j] * mI[j];
        }
        var ma = boxBlur(a, w, h, r), mb = boxBlur(b, w, h, r), q = mI;
        for (var k = 0; k < n; k++) q[k] = ma[k] * I[k] + mb[k];
        return q;
    }
    // Gaussian by three box passes (σ² ≈ r(r+1)) — the smooth surround lightness.
    function gaussianish(src, w, h, sigma) {
        var r = Math.max(1, Math.round(Math.sqrt(sigma * sigma + 0.25) - 0.5));
        var a = boxBlur(src, w, h, r), b = boxBlur(a, w, h, r);
        return boxBlur(b, w, h, r, a);
    }

    function luminance(rgba, w, h) {
        var n = w * h, Y = new Float32Array(n);
        for (var i = 0, o = 0; i < n; i++, o += 4) Y[i] = 0.2126 * LIN[rgba[o]] + 0.7152 * LIN[rgba[o + 1]] + 0.0722 * LIN[rgba[o + 2]];
        return Y;
    }

    // Percentile of linear luminances (0..~1.5) by a 16384-bin histogram —
    // O(n); sorting a million floats per tap cost 0.8 s (profiled 21.10.2026).
    var PCT_BINS = 16384, PCT_MAX = 1.5;
    function percentile(vals, n, pct) {
        if (!n) return 0;
        var hist = new Uint32Array(PCT_BINS), k = PCT_BINS / PCT_MAX;
        for (var i = 0; i < n; i++) {
            var b = (vals[i] * k) | 0;
            hist[b < 0 ? 0 : (b >= PCT_BINS ? PCT_BINS - 1 : b)]++;
        }
        var want = Math.min(n - 1, Math.max(0, Math.round(pct / 100 * (n - 1)))), acc = 0;
        for (var j = 0; j < PCT_BINS; j++) { acc += hist[j]; if (acc > want) return (j + 0.5) / k; }
        return PCT_MAX;
    }

    /* ── «only the main colour» features ───────────────────────────────────── */
    var PAT_K = 4, PAT_MERGE = 0.12, PAT_WL = 0.175;
    var PAT_NEUTRAL = 0.03, PAT_SHADE = [0.5, 1.6], PAT_DARK = 0.06;
    var PAT_SHADE_HUE = 45 * Math.PI / 180, PAT_SHADE_DARK = 0.85;
    /* Glossy paint: the material takes its reflection (29.10.2026, docs/why/primitives/object-paint.md).
       A pixel of glossy paint is I = s·body + h·light. Shading (s) scales the chroma vector I − Y of
       LINEAR light, reflected light (h) adds no chroma (or a faint sky tint) — so neither turns that
       vector: a paler cluster on the same line is the same paint with sky in it, wherever the tap
       landed. A print or a second tone is another pigment and turns it: the pale-blue sea print
       of a Hawaiian shirt 17°, a car's pale reflection 0.5–6.5° (5 cars, 2 apples). */
    var GLOSS_JOIN_DIR = Math.cos(10 * Math.PI / 180), GLOSS_JOIN_C = [0.2, 4], GLOSS_BODY_C = 0.6;
    // per pixel, once the material has shown its gloss: light (≥ 0.8 of the lit level), still carrying
    // ≥ 0.15 of the body's colour, with a tint across the body's line ≤ 0.2 of its luminance (sky)
    var REACH_Y = 0.8, REACH_Q = 0.15, REACH_TINT = 0.2;
    // Writes the 3 features of pixel (r,g,b bytes) with surround lightness lsm into f.
    var OK = [0, 0, 0];
    function featureOf(r, g, b, lsm, f) {
        var ok = C.oklabInto(LIN[r], LIN[g], LIN[b], OK);
        var Lc = ok[0] > 0.15 ? ok[0] : 0.15;
        var rel = ok[0] / (lsm > 0.05 ? lsm : 0.05);
        if (rel > 2.5) rel = 2.5;
        f[0] = ok[1] / Lc; f[1] = ok[2] / Lc; f[2] = PAT_WL * rel;
        return f;
    }
    function smoothstep(e0, e1, x) { var t = (x - e0) / (e1 - e0); t = t < 0 ? 0 : (t > 1 ? 1 : t); return t * t * (3 - 2 * t); }

    // Deterministic k-means++ (seeded LCG) on a subsample.
    function kmeans(F, n, k, iters) {
        var seed = 12345;
        function rnd() { seed = (seed * 1103515245 + 12345) >>> 0; return seed / 4294967296; }
        var cent = new Float64Array(k * 3), d2 = new Float64Array(n);
        var first = Math.floor(rnd() * n);
        cent[0] = F[first * 3]; cent[1] = F[first * 3 + 1]; cent[2] = F[first * 3 + 2];
        for (var c = 1; c < k; c++) {
            var sum = 0;
            for (var i = 0; i < n; i++) {
                var best = Infinity;
                for (var j = 0; j < c; j++) {
                    var dx = F[i * 3] - cent[j * 3], dy = F[i * 3 + 1] - cent[j * 3 + 1], dz = F[i * 3 + 2] - cent[j * 3 + 2];
                    var dd = dx * dx + dy * dy + dz * dz; if (dd < best) best = dd;
                }
                d2[i] = best; sum += best;
            }
            var pick = rnd() * sum, idx = 0;
            for (var i2 = 0; i2 < n; i2++) { pick -= d2[i2]; if (pick <= 0) { idx = i2; break; } }
            cent[c * 3] = F[idx * 3]; cent[c * 3 + 1] = F[idx * 3 + 1]; cent[c * 3 + 2] = F[idx * 3 + 2];
        }
        var lab = new Int32Array(n), cnt = new Float64Array(k);
        for (var it = 0; it < iters; it++) {
            var acc = new Float64Array(k * 3); cnt.fill(0);
            for (var p = 0; p < n; p++) {
                var bi = 0, bd = Infinity;
                for (var q = 0; q < k; q++) {
                    var ex = F[p * 3] - cent[q * 3], ey = F[p * 3 + 1] - cent[q * 3 + 1], ez = F[p * 3 + 2] - cent[q * 3 + 2];
                    var de = ex * ex + ey * ey + ez * ez; if (de < bd) { bd = de; bi = q; }
                }
                lab[p] = bi; cnt[bi]++; acc[bi * 3] += F[p * 3]; acc[bi * 3 + 1] += F[p * 3 + 1]; acc[bi * 3 + 2] += F[p * 3 + 2];
            }
            for (var s = 0; s < k; s++) if (cnt[s] > 0) { cent[s * 3] = acc[s * 3] / cnt[s]; cent[s * 3 + 1] = acc[s * 3 + 1] / cnt[s]; cent[s * 3 + 2] = acc[s * 3 + 2] / cnt[s]; }
        }
        return { cent: cent, cnt: cnt };
    }

    /* ── analysis of one object at the working size ───────────────────────── */
    // Everything heavy runs on the object's box plus a margin: the filters'
    // windows reach r px, and outside the object the mask is zero, so the
    // answer inside is the same as on the whole picture.
    function analyze(o) {
        var w = o.w, h = o.h, mask = o.mask, x0 = w, y0 = h, x1 = -1, y1 = -1;
        for (var y = 0; y < h; y++) {
            var row = y * w;
            for (var x = 0; x < w; x++) if (mask[row + x]) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
        }
        if (x1 < 0) return null;
        var pad = Math.max(24, Math.round(24 * Math.max(w, h) / 1280));
        x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad); x1 = Math.min(w - 1, x1 + pad); y1 = Math.min(h - 1, y1 + pad);
        var cw = x1 - x0 + 1, ch = y1 - y0 + 1;
        var crgba = new Uint8Array(cw * ch * 4), cmask = new Uint8Array(cw * ch);
        for (var yy = 0; yy < ch; yy++) {
            var so = ((y0 + yy) * w + x0);
            crgba.set(o.rgba.subarray(so * 4, (so + cw) * 4), yy * cw * 4);
            cmask.set(mask.subarray(so, so + cw), yy * cw);
        }
        var an = analyzeBox({ rgba: crgba, w: cw, h: ch, mask: cmask, imageGrey: o.imageGrey, scaleW: Math.max(w, h),
            tap: o.tap ? { x: o.tap.x - x0, y: o.tap.y - y0 } : null });
        if (!an) return null;
        var alpha = new Float32Array(w * h), Lsm = new Float32Array(w * h);
        for (var y2 = 0; y2 < ch; y2++) {
            alpha.set(an.alpha.subarray(y2 * cw, (y2 + 1) * cw), (y0 + y2) * w + x0);
            Lsm.set(an.Lsm.subarray(y2 * cw, (y2 + 1) * cw), (y0 + y2) * w + x0);
        }
        an.alpha = alpha; an.Lsm = Lsm; an.w = w; an.h = h; an.box = { x: x0, y: y0, w: cw, h: ch };
        return an;
    }
    function analyzeBox(o) {
        var rgba = o.rgba, w = o.w, h = o.h, mask = o.mask, n = w * h;
        var Y = luminance(rgba, w, h), M = new Float32Array(n), area = 0;
        for (var i = 0; i < n; i++) if (mask[i]) { M[i] = 1; area++; }
        if (area < 16) return null;
        var scale = (o.scaleW || Math.max(w, h)) / 1280;
        /* The mask's edge, put ON the picture's edge by colour (the shared
           MaskEdge, /video-bg-remover's «keep» decision). Measured 21.10.2026:
           an inward-only feather (the lab's first answer: the ring outside
           untouched) left the segmenter's 1–3 px short edge in the OLD colour —
           a red rim round a dress turned emerald; a colour-blind outward band
           fixed that and painted the dark floor next to a black sofa cream
           (ring ΔE 136). Here a pixel of the edge band joins the side whose
           LOCAL mean colour it is closer to, and where both sides share a
           colour the mask stands as drawn. */
        var R = Math.max(2, Math.round(3 * scale));
        var alpha = (ME && ME.refineEdge) ? ME.refineEdge(M, rgba, w, h, R) : null;
        if (!alpha) { alpha = new Float32Array(n); for (var j = 0; j < n; j++) alpha[j] = M[j]; }
        /* An edge pixel is a MIX of the object and what is behind it; painted
           at its mix share it keeps part of the old colour — a pink line round
           an emerald dress. Mostly-object pixels (≥ 0.6 on the colour decision)
           take the whole new colour; relight keeps the mixed pixel's own
           lightness, so the edge stays antialiased by brightness. */
        for (var ja = 0; ja < n; ja++) { var av = alpha[ja]; if (av > 0 && av < 1) alpha[ja] = smoothstep(0.2, 0.6, av); }
        // smooth surround lightness for the pattern features: OKLab L, averaged
        // over the object only (σ grows with the object, ≥ 8 px)
        var Lok = new Float32Array(n), LM = new Float32Array(n);
        for (var q = 0, o4 = 0; q < n; q++, o4 += 4) {
            if (!M[q]) continue;
            Lok[q] = C.oklabInto(LIN[rgba[o4]], LIN[rgba[o4 + 1]], LIN[rgba[o4 + 2]], OK)[0];
            LM[q] = Lok[q];
        }
        var sig = Math.max(8 * scale, 0.12 * Math.sqrt(area));
        var num = gaussianish(LM, w, h, sig), den = gaussianish(M, w, h, sig);
        var Lsm = new Float32Array(n);
        for (var u = 0; u < n; u++) Lsm[u] = den[u] > 1e-6 ? num[u] / den[u] : 0;
        // features, subsampled to ≤ 30 000 for the clustering
        var step = Math.max(1, Math.floor(Math.sqrt(area / 30000))), F = [], CH = [], DL = [], f = [0, 0, 0];
        for (var yy = 0; yy < h; yy += step) for (var xx = 0; xx < w; xx += step) {
            var p = yy * w + xx; if (!M[p]) continue;
            featureOf(rgba[p * 4], rgba[p * 4 + 1], rgba[p * 4 + 2], Lsm[p], f);
            F.push(f[0], f[1], f[2]);
            CH.push(Math.hypot(OK[1], OK[2]));      // absolute OKLab chroma (featureOf left it in OK)
            var lr = LIN[rgba[p * 4]], lg = LIN[rgba[p * 4 + 1]], lb = LIN[rgba[p * 4 + 2]];
            DL.push((lr - lg) * 0.70710678, (lr + lg - 2 * lb) * 0.40824829);   // I − Y of linear light, in the plane ⟂ grey
        }
        var nf = F.length / 3, km = kmeans(F, nf, Math.min(PAT_K, Math.max(1, nf)), 20);
        var k = km.cnt.length;
        // each cluster's median ABSOLUTE chroma: the feature divides by L, which
        // blows a near-grey shadow up to «chromatic» (black leather's shadows:
        // 0.19 in the feature, ~0.02 in fact — the sofa came out blotchy)
        var chBy = [], dir = new Float64Array(k * 2); for (var c0 = 0; c0 < k; c0++) chBy.push([]);
        for (var pi = 0; pi < nf; pi++) {
            var bk = 0, bdk = Infinity;
            for (var c1 = 0; c1 < k; c1++) {
                var e0 = F[pi * 3] - km.cent[c1 * 3], e1 = F[pi * 3 + 1] - km.cent[c1 * 3 + 1], e2 = F[pi * 3 + 2] - km.cent[c1 * 3 + 2];
                var de = e0 * e0 + e1 * e1 + e2 * e2; if (de < bdk) { bdk = de; bk = c1; }
            }
            chBy[bk].push(CH[pi]);
            dir[bk * 2] += DL[pi * 2]; dir[bk * 2 + 1] += DL[pi * 2 + 1];
        }
        var chroma = chBy.map(function (a) { if (!a.length) return 0; a.sort(function (x, y) { return x - y; }); return a[a.length >> 1]; });
        // agglomerative merge of near clusters → groups
        var group = []; for (var gi = 0; gi < k; gi++) group.push(gi);
        var gc = {}; for (var g2 = 0; g2 < k; g2++) gc[g2] = { c: [km.cent[g2 * 3], km.cent[g2 * 3 + 1], km.cent[g2 * 3 + 2]], n: km.cnt[g2] };
        for (;;) {
            var keys = Object.keys(gc).map(Number); if (keys.length < 2) break;
            var bestD = Infinity, pa = -1, pb = -1;
            for (var a1 = 0; a1 < keys.length; a1++) for (var b1 = a1 + 1; b1 < keys.length; b1++) {
                var A = gc[keys[a1]].c, B = gc[keys[b1]].c;
                var dd = Math.hypot(A[0] - B[0], A[1] - B[1], A[2] - B[2]);
                if (dd < bestD) { bestD = dd; pa = keys[a1]; pb = keys[b1]; }
            }
            if (bestD >= PAT_MERGE) break;
            var na = gc[pa].n, nb = gc[pb].n, tot = Math.max(1, na + nb);
            gc[pa] = { c: [0, 1, 2].map(function (t) { return (gc[pa].c[t] * na + gc[pb].c[t] * nb) / tot; }), n: na + nb };
            delete gc[pb];
            for (var gg = 0; gg < group.length; gg++) if (group[gg] === pb) group[gg] = pa;
        }
        // the group under the tap (7×7 median of features), else the largest
        var selGroup = -1;
        if (o.tap) {
            var tx = Math.round(o.tap.x), ty = Math.round(o.tap.y), fx = [], fy = [], fz = [];
            for (var dy = -3; dy <= 3; dy++) for (var dx = -3; dx <= 3; dx++) {
                var px = tx + dx, py = ty + dy;
                if (px < 0 || py < 0 || px >= w || py >= h) continue;
                var pp = py * w + px;
                featureOf(rgba[pp * 4], rgba[pp * 4 + 1], rgba[pp * 4 + 2], Lsm[pp], f);
                fx.push(f[0]); fy.push(f[1]); fz.push(f[2]);
            }
            if (fx.length) {
                var med = [fx, fy, fz].map(function (arr) { arr.sort(function (x, y) { return x - y; }); return arr[arr.length >> 1]; });
                var bi = 0, bd = Infinity;
                for (var c3 = 0; c3 < k; c3++) {
                    var e = Math.hypot(med[0] - km.cent[c3 * 3], med[1] - km.cent[c3 * 3 + 1], med[2] - km.cent[c3 * 3 + 2]);
                    if (e < bd) { bd = e; bi = c3; }
                }
                selGroup = group[bi];
            }
        }
        if (selGroup < 0) {
            var sizes = {}; for (var s2 = 0; s2 < k; s2++) sizes[group[s2]] = (sizes[group[s2]] || 0) + km.cnt[s2];
            var top = -1; for (var key in sizes) if (top < 0 || sizes[key] > sizes[top]) top = +key;
            selGroup = top;
        }
        var sel = new Uint8Array(k); for (var s3 = 0; s3 < k; s3++) sel[s3] = group[s3] === selGroup ? 1 : 0;
        /* A NEUTRAL material (white, grey, black) has nothing but lightness to
           tell its parts apart — and on it a lightness difference is light and
           shade, not a pattern. So the clusters of a neutral pick take in every
           other neutral cluster within the range of shading; a very dark one
           stays out (a white car's tyres), and so does a specular one (chrome,
           gloss). Chromatic parts — stripes, a logo, a tab — stay out as before.
           Measured 21.10.2026: black leather sofa → cream went blotchy without
           this (its shadows were left black). */
        var neutral = true;
        for (var s4 = 0; s4 < k; s4++) if (sel[s4] && chroma[s4] >= PAT_NEUTRAL) neutral = false;
        if (neutral) {
            for (var s5 = 0; s5 < k; s5++) {
                if (sel[s5] || chroma[s5] >= PAT_NEUTRAL) continue;
                var lrel = km.cent[s5 * 3 + 2] / PAT_WL;
                if (lrel >= PAT_SHADE[0] && lrel <= PAT_SHADE[1]) sel[s5] = 1;
            }
        } else {
            /* A SATURATED material in deep shade shifts hue on a photo — a yellow
               raincoat's folds come out orange (hue 89° lit → 50° in shade,
               lightness 1.02 → 0.62) and stayed orange stripes on a navy coat
               (21.10.2026). A clearly darker cluster within 45° of the picked hue
               and of comparable saturation is the same material in shade; a
               stripe, print or logo is the other colour at its own lightness, or
               a different hue, and stays out. */
            var sa = 0, sb = 0, sl = 0, sn = 0;
            for (var s6 = 0; s6 < k; s6++) if (sel[s6]) {
                var wn = km.cnt[s6]; sa += km.cent[s6 * 3] * wn; sb += km.cent[s6 * 3 + 1] * wn; sl += km.cent[s6 * 3 + 2] * wn; sn += wn;
            }
            if (sn > 0) {
                sa /= sn; sb /= sn; sl /= sn;
                var sHue = Math.atan2(sb, sa), sC = Math.hypot(sa, sb);
                for (var s7 = 0; s7 < k; s7++) {
                    if (sel[s7] || chroma[s7] < PAT_NEUTRAL) continue;
                    var ca = km.cent[s7 * 3], cb = km.cent[s7 * 3 + 1], cC = Math.hypot(ca, cb);
                    var dh = Math.abs(Math.atan2(cb, ca) - sHue); if (dh > Math.PI) dh = 2 * Math.PI - dh;
                    if (dh <= PAT_SHADE_HUE && km.cent[s7 * 3 + 2] <= PAT_SHADE_DARK * sl && cC >= 0.5 * sC && cC <= 2 * sC) sel[s7] = 1;
                }
            }
        }
        /* GLOSS: a chromatic material takes its own reflection — the paler (or, when the tap
           landed on the reflection, the deeper) cluster whose linear chroma vector points the same
           way. Without it «only the main colour» left the sky-lit half of a red car red (Mazda 6,
           Renault Captur, Kia Rio, Ford Focus, Lada Niva: 13–31 % of the car in the old colour). */
        var glossJoin = 0, calib = null;
        if (!neutral) {
            var ga = 0, gb = 0, gn = 0, gd1 = 0, gd2 = 0;
            for (var g1 = 0; g1 < k; g1++) if (sel[g1]) { ga += km.cent[g1 * 3] * km.cnt[g1]; gb += km.cent[g1 * 3 + 1] * km.cnt[g1]; gn += km.cnt[g1]; gd1 += dir[g1 * 2]; gd2 += dir[g1 * 2 + 1]; }
            var gC = gn > 0 ? Math.hypot(ga / gn, gb / gn) : 0, gd = Math.hypot(gd1, gd2);
            for (var g2 = 0; g2 < k && gd > 0; g2++) {
                if (sel[g2] || chroma[g2] < PAT_NEUTRAL) continue;
                var qC = Math.hypot(km.cent[g2 * 3], km.cent[g2 * 3 + 1]), qd = Math.hypot(dir[g2 * 2], dir[g2 * 2 + 1]);
                if (qd > 0 && (dir[g2 * 2] * gd1 + dir[g2 * 2 + 1] * gd2) / (qd * gd) >= GLOSS_JOIN_DIR && qC >= GLOSS_JOIN_C[0] * gC && qC <= GLOSS_JOIN_C[1] * gC) { sel[g2] = 1; glossJoin++; }
            }
            /* With its reflection in, the material's lit band would be the reflection: the gloss
               split (glossOf → relightPixel) would take the sky for the body and paint it solid —
               synthetic paint ΔE 16.5 instead of 15.6. So the lit level and the body's colour are
               read on the BODY: the clusters at ≥ 0.6 of the material's richest chroma. */
            if (glossJoin) {
                var maxC = 0;
                for (var g3 = 0; g3 < k; g3++) if (sel[g3] && chroma[g3] >= PAT_NEUTRAL) maxC = Math.max(maxC, Math.hypot(km.cent[g3 * 3], km.cent[g3 * 3 + 1]));
                calib = new Uint8Array(k);
                for (var g4 = 0; g4 < k; g4++) calib[g4] = sel[g4] && Math.hypot(km.cent[g4 * 3], km.cent[g4 * 3 + 1]) >= GLOSS_BODY_C * maxC ? 1 : 0;
            }
        }
        var distinct = Object.keys(gc).length;
        var an = { w: w, h: h, alpha: alpha, Lsm: Lsm, cent: new Float32Array(km.cent), sel: sel, k: k, groups: distinct, area: area, chroma: chroma, neutral: neutral };
        // lit level — over the object, and over its main colour only
        var Ys = guided(Y, Y, w, h, Math.max(1, Math.round(8 * scale)), 4e-4);
        var vAll = new Float32Array(area), vSel = new Float32Array(area), vY = new Float32Array(area), nAll = 0, nSel = 0, ff = [0, 0, 0];
        var inSel = new Uint8Array(n);
        for (var z = 0; z < n; z++) {
            if (alpha[z] <= 0.5) continue;
            vY[nAll] = Y[z];
            vAll[nAll++] = Ys[z];
            if (patternWeightAt(an, rgba[z * 4], rgba[z * 4 + 1], rgba[z * 4 + 2], Lsm[z], ff) > 0.5) {
                if (calib) {            // the body only (see GLOSS above)
                    var nb = 0, nd = Infinity;
                    for (var cc = 0; cc < k; cc++) {
                        var e3 = ff[0] - km.cent[cc * 3], e4 = ff[1] - km.cent[cc * 3 + 1], e5 = ff[2] - km.cent[cc * 3 + 2], dd2 = e3 * e3 + e4 * e4 + e5 * e5;
                        if (dd2 < nd) { nd = dd2; nb = cc; }
                    }
                    if (!calib[nb]) continue;
                }
                vSel[nSel++] = Ys[z]; inSel[z] = 1;
            }
        }
        an.litAll = Math.max(1e-4, percentile(vAll, nAll, 70));
        an.litSel = nSel >= 50 ? Math.max(1e-4, percentile(vSel, nSel, 70)) : an.litAll;
        an.yMed = percentile(vY, nAll, 50);
        an.glossAll = glossOf(rgba, Ys, alpha, null, n, percentile(vAll, nAll, GLOSS_BAND[0]), percentile(vAll, nAll, GLOSS_BAND[1]));
        an.glossSel = nSel >= 50 ? glossOf(rgba, Ys, alpha, inSel, n, percentile(vSel, nSel, GLOSS_BAND[0]), percentile(vSel, nSel, GLOSS_BAND[1])) : an.glossAll;
        /* The k = 4 clusters cut the reflection's continuum somewhere: the palest, brightest
           reflections (sky on a hood) fall nearer a grey cluster and stayed in the old colour.
           On a material that has shown its gloss, a light pixel that still carries the body's
           colour along its line, with no more than a sky tint across it, is that paint too. */
        an.glossJoin = glossJoin;
        if (glossJoin && an.glossSel.kb > 0) {
            var u1 = 0, u2 = 0;
            for (var gu = 0; gu < k; gu++) if (calib[gu]) { u1 += dir[gu * 2]; u2 += dir[gu * 2 + 1]; }
            var un = Math.hypot(u1, u2);
            if (un > 0) an.reach = { u1: u1 / un, u2: u2 / un, y: REACH_Y * an.litSel, kb: an.glossSel.kb };
        }
        /* The default of «only the main colour». Off for a DARK neutral material
           (black leather, black paint): its light and dark are reflections, and
           splitting them left a cream sofa with black stars in every fold and a
           white car with black flanks; the whole object read right (21.10.2026,
           median luminance 0.011 and 0.049; the white cars, 0.32–0.33, keep it on
           — it is what spares a grille, headlights and the number plate). */
        an.defaultKeep = !o.imageGrey && !(an.neutral && an.yMed < PAT_DARK);   // on a B&W picture lightness is all there is — splitting by it is splitting light from shade
        // Black-and-white is a verdict on the PICTURE, not on the object: a white
        // car in a colour photo has no chroma either, and keeping its lightness
        // would leave it white whatever colour is asked (lab, 21.10.2026). The
        // page asks ColorizeEngine.colourVerdict and passes the answer in.
        an.grey = !!o.imageGrey;
        return an;
    }

    /* ── the lit body's colour, for the gloss split in OpColor.relightPixel ──
       kb — the median chroma-per-luminance |I − Y|/Y (linear light) of the
       pixels in the lit band (60–80th percentile of the smoothed luminance):
       a pixel paler than that is part reflection. conf — whether the body HAS
       a colour to tell the reflection by: OKLab chroma of the band's median
       colour, off below 0.03, full above 0.07. 12 library car photos: white,
       silver and black bodies 0.003–0.026 (their «chroma» is the sky in the
       paint or noise — the split ruined them), coloured 0.08–0.20.
       Measured 29.10.2026 (docs/why/primitives/object-paint.md): the 95th
       percentile instead of the median scored better on synthetic renders but
       turned a red car's yellow a muddy mustard — the lit band must stay the
       swatch. */
    var GLOSS_BAND = [60, 80], GLOSS_CONF = [0.03, 0.07], GLOSS_N = 20000;
    function glossOf(rgba, Ys, alpha, inSel, n, lo, hi) {
        var cnt = 0;
        for (var i = 0; i < n; i++) if (alpha[i] > 0.5 && (!inSel || inSel[i]) && Ys[i] >= lo && Ys[i] <= hi) cnt++;
        if (cnt < 20) return { kb: 0, conf: 0 };
        var step = Math.max(1, Math.ceil(cnt / GLOSS_N)), m = Math.ceil(cnt / step);
        var K = new Float32Array(m), R = new Float32Array(m), G = new Float32Array(m), B = new Float32Array(m), k = 0, c = 0;
        for (var j = 0; j < n && k < m; j++) {
            if (!(alpha[j] > 0.5 && (!inSel || inSel[j]) && Ys[j] >= lo && Ys[j] <= hi)) continue;
            if (c++ % step) continue;
            var r = LIN[rgba[j * 4]], g = LIN[rgba[j * 4 + 1]], b = LIN[rgba[j * 4 + 2]], y = C.lumY(r, g, b);
            K[k] = y > 1e-4 ? Math.sqrt((r - y) * (r - y) + (g - y) * (g - y) + (b - y) * (b - y)) / y : 0;
            R[k] = r; G[k] = g; B[k] = b; k++;
        }
        function med(a) { a = a.subarray(0, k); a.sort(); return a[k >> 1]; }
        var ok = C.linearToOklab(med(R), med(G), med(B));
        return { kb: med(K), conf: smoothstep(GLOSS_CONF[0], GLOSS_CONF[1], Math.hypot(ok[1], ok[2])) };
    }

    /* The weight of one pixel, 0..1: the edge-refined mask alpha `a` (sampled
       at this pixel), times the «main colour» weight when keepPattern. ONE
       function for weights(), paint() and paintRows(): preview = export. */
    function weightAt(an, r, g, b, a, lsm, keepPattern, f) {
        if (a <= 0.004) return 0;
        return keepPattern ? a * patternWeightAt(an, r, g, b, lsm, f) : a;
    }

    // The «main colour» weight of one pixel, 0..1.
    function patternWeightAt(an, r, g, b, lsm, f) {
        featureOf(r, g, b, lsm, f);
        if (an.groups < 2) return 1;
        var dSel = Infinity, dOth = Infinity;
        for (var c = 0; c < an.k; c++) {
            var d = Math.hypot(f[0] - an.cent[c * 3], f[1] - an.cent[c * 3 + 1], f[2] - an.cent[c * 3 + 2]);
            if (an.sel[c]) { if (d < dSel) dSel = d; } else if (d < dOth) dOth = d;
        }
        if (dOth === Infinity) return 1;
        var wt = 1 - smoothstep(0.38, 0.62, dSel / Math.max(1e-9, dSel + dOth));
        var R = an.reach;           // glossy paint's palest reflections (see analyzeBox)
        if (R && wt < 1) {
            var lr = LIN[r], lg = LIN[g], lb = LIN[b], y = 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
            var wy = smoothstep(0.75 * R.y, R.y, y);
            if (wy > 0) {
                var c1 = (lr - lg) * 0.70710678, c2 = (lr + lg - 2 * lb) * 0.40824829;
                var along = (c1 * R.u1 + c2 * R.u2) / (y * R.kb), across = Math.abs(c2 * R.u1 - c1 * R.u2) / y;
                var wr = wy * smoothstep(0.5 * REACH_Q, REACH_Q, along) * (1 - smoothstep(0.75 * REACH_TINT, 1.25 * REACH_TINT, across));
                if (wr > wt) wt = wr;
            }
        }
        return wt;
    }

    /* ── a reference region → the colour it teaches ───────────────────────── */
    function referenceOf(o) {
        var rgba = o.rgba, w = o.w, h = o.h, mask = o.mask, n = w * h;
        var Ls = [], As = [], Bs = [], ys = [], px = [], m = 0;
        for (var i = 0; i < n; i++) {
            if (!mask[i]) continue;
            var r = LIN[rgba[i * 4]], g = LIN[rgba[i * 4 + 1]], b = LIN[rgba[i * 4 + 2]];
            var lab = C.linearToLab(r, g, b);
            Ls.push(lab[0]); As.push(lab[1]); Bs.push(lab[2]);
            ys.push(C.lumY(r, g, b)); px.push(i);
            m++;
        }
        if (m < 16) return null;
        var lut = C.buildLut(Ls, As, Bs, m);
        // the swatch shown to the person and used by relight: the median colour
        // of the region's lit pixels (40–80th luminance percentile), taken in Lab
        // so the three medians belong to one colour
        var order = ys.map(function (y, k) { return k; }).sort(function (a, b) { return ys[a] - ys[b]; });
        var lo = Math.floor(order.length * 0.4), hi = Math.max(lo + 1, Math.floor(order.length * 0.8));
        var lL = [], la = [], lb = [];
        for (var q = lo; q < hi; q++) { var k = order[q]; lL.push(Ls[k]); la.push(As[k]); lb.push(Bs[k]); }
        function med(a) { a.sort(function (x, y) { return x - y; }); return a[a.length >> 1]; }
        var lin = C.labToLinear(med(lL), med(la), med(lb)), o3 = [0, 0, 0];
        C.clipKeepY(lin[0], lin[1], lin[2], o3);
        var rgb = o3.map(function (v) { return Math.round(C.linearToSrgb(v) * 255); });
        return { rgb: rgb, lut: lut, yMed: ys[order[order.length >> 1]], small: m < 400, px: m };
    }

    /* ── painter params for one layer ──────────────────────────────────────
       colour = {rgb:[r,g,b], mode:'relight'|'tint'|'lut', lut?, yMed?, bright?}
       mode 'auto': a colour object is relit; a black-and-white one keeps the
       film's lightness — tinted by a picked colour, or by the reference table. */
    /* Can the photo's own lightness carry this colour? «Keep the lightness»
       (tint) leaves L as it is, so black stays black whatever hue it is given
       and near-white stays near-white: a colour photo of a black sofa by a
       white wall, judged black-and-white, was asked for cream and came out
       black (22.10.2026). The lightness carries the colour when the gap to the
       colour's own lightness is under TINT_DL (OKLab) and the gamut at the
       object's lightness holds at least half of the colour's chroma. A grey of
       luminance Y has OKLab L = ∛Y. */
    var TINT_DL = 0.35, TINT_CHROMA = 0.5;
    function tintCarries(an, rgb) {
        var t = C.rgbToOklab(rgb[0], rgb[1], rgb[2]), Ct = Math.hypot(t[1], t[2]);
        var Lo = Math.cbrt(Math.max(0, an.yMed || 0));
        if (Math.abs(t[0] - Lo) > TINT_DL) return false;
        return Ct < 0.04 || C.maxChroma(Lo, t[1], t[2]) >= TINT_CHROMA * Ct;
    }
    function prepare(an, colour, keepPattern) {
        var mode = colour.mode || 'auto';
        if (mode === 'auto') mode = an.grey ? (colour.lut ? 'lut' : 'tint') : 'relight';
        var lit = keepPattern ? an.litSel : an.litAll;
        if (mode === 'relight') return { mode: 'relight', R: C.relightParams(colour.rgb, lit, keepPattern ? an.glossSel : an.glossAll) };
        if (mode === 'tint') return { mode: 'tint', R: C.tintParams(colour.rgb) };
        var b = +colour.bright || 0, ys = 1;
        if (b > 0 && colour.yMed > 0 && an.yMed > 0) ys = Math.pow(colour.yMed / an.yMed, b);
        return { mode: 'lut', R: { lut: colour.lut, yScale: ys } };
    }

    /* ── bilinear sample of a w×h plane at fractional (x, y) ──────────────── */
    function sample(plane, w, h, x, y) {
        if (x < 0) x = 0; if (y < 0) y = 0;
        if (x > w - 1) x = w - 1; if (y > h - 1) y = h - 1;
        var x0 = x | 0, y0 = y | 0, x1 = x0 + 1 < w ? x0 + 1 : x0, y1 = y0 + 1 < h ? y0 + 1 : y0, fx = x - x0, fy = y - y0;
        var a = plane[y0 * w + x0], b = plane[y0 * w + x1], c = plane[y1 * w + x0], d = plane[y1 * w + x1];
        return (a + (b - a) * fx) + ((c + (d - c) * fx) - (a + (b - a) * fx)) * fy;
    }

    /* ── paint: src RGBA → dst RGBA at w×h, layers in order ──────────────────
       layer = {an, P (from prepare), keepPattern, strength 0..1}
       The analysis may be at another size: alpha and the surround lightness are
       sampled bilinearly, everything else is decided at THIS size. Outside the
       object a pixel is never touched (its refined alpha is 0 there). */
    /* The object's weight at THIS size: mask alpha × «main colour» weight. It
       does not depend on the colour, so the page computes it once per (object,
       size, «main colour» switch) and every colour change after that is the
       per-pixel colour arithmetic only. Returns {W, x0, y0, x1, y1, edge} —
       the box where W > 0 (paint() walks only it) and the EDGE PLANES below. */
    var EDGE_LO = 0.02, EDGE_HI = 0.98, EDGE_OFFLINE = 0.15;
    function weights(an, src, w, h, keepPattern) {
        var W = new Float32Array(w * h), IN = new Uint8Array(w * h), f = [0, 0, 0];
        var x0 = w, y0 = h, x1 = -1, y1 = -1;
        var sx = an.w / w, sy = an.h / h;
        for (var y = 0; y < h; y++) {
            var ay = (y + 0.5) * sy - 0.5;
            for (var x = 0; x < w; x++) {
                var ax = (x + 0.5) * sx - 0.5;
                var a0 = sample(an.alpha, an.w, an.h, ax, ay);
                if (a0 <= 0.004) continue;
                IN[y * w + x] = 1;
                var i = (y * w + x) * 4;
                var a = weightAt(an, src[i], src[i + 1], src[i + 2], a0, sample(an.Lsm, an.w, an.h, ax, ay), keepPattern, f);
                if (a <= 0.004) continue;
                W[y * w + x] = a;
                if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
            }
        }
        var out = { W: W, IN: IN, w: w, h: h, x0: x0, y0: y0, x1: x1, y1: y1, keepPattern: !!keepPattern, edge: null };
        if (x1 >= 0) out.edge = edgePlanes(src, w, h, W, x0, y0, x1, y1);
        return out;
    }

    /* ── edge decontamination ─────────────────────────────────────────────
       A pixel on the object's edge (or on the line between the tapped colour
       and the next one) is a MIX: C = αF + (1−α)B. Recoloured whole and then
       blended by its weight it keeps part of the old colour — a pink line
       between an emerald dress and the skin (21.10.2026). The edge planes hold,
       for every pixel near an edge, the local mean of the PURE object (W ≥
       0.98) and of the pure rest (W ≤ 0.02) within r px; paint() takes α as
       the projection of C on B→F and replaces only the object's share:
       out = C + α·(recolour(F) − F). Where either side has no pure pixel
       nearby, or F and B are one colour, the pixel falls back to its weight. */
    function edgePlanes(src, w, h, W, x0, y0, x1, y1) {
        var r = Math.max(2, Math.round(4 * Math.max(w, h) / 1280));
        var bx0 = Math.max(0, x0 - r), by0 = Math.max(0, y0 - r), bx1 = Math.min(w - 1, x1 + r), by1 = Math.min(h - 1, y1 + r);
        var bw = bx1 - bx0 + 1, bh = by1 - by0 + 1, n = bw * bh;
        var mF = new Float32Array(n), mB = new Float32Array(n), any = false;
        var FR = new Float32Array(n), FG = new Float32Array(n), FB = new Float32Array(n);
        var BR = new Float32Array(n), BG = new Float32Array(n), BB = new Float32Array(n);
        for (var y = 0; y < bh; y++) for (var x = 0; x < bw; x++) {
            var q = y * bw + x, p = (by0 + y) * w + (bx0 + x), o = p * 4, wv = W[p];
            var lr = LIN[src[o]], lg = LIN[src[o + 1]], lb = LIN[src[o + 2]];
            if (wv >= EDGE_HI) { mF[q] = 1; FR[q] = lr; FG[q] = lg; FB[q] = lb; }
            else if (wv <= EDGE_LO) { mB[q] = 1; BR[q] = lr; BG[q] = lg; BB[q] = lb; }
            else any = true;
        }
        if (!any) return null;
        // the object's material as a whole: for an edge with no pure object
        // pixel within r — a 3-px shoulder strap, hair strands over a dress
        var gF = [0, 0, 0], gn = 0;
        for (var g0 = 0; g0 < n; g0++) if (mF[g0]) { gF[0] += FR[g0]; gF[1] += FG[g0]; gF[2] += FB[g0]; gn++; }
        if (gn) { gF[0] /= gn; gF[1] /= gn; gF[2] /= gn; }
        var cF = boxBlur(mF, bw, bh, r), cB = boxBlur(mB, bw, bh, r);
        FR = boxBlur(FR, bw, bh, r); FG = boxBlur(FG, bw, bh, r); FB = boxBlur(FB, bw, bh, r);
        BR = boxBlur(BR, bw, bh, r); BG = boxBlur(BG, bw, bh, r); BB = boxBlur(BB, bw, bh, r);
        // linear means, kept only where both sides were seen; NaN = no decision
        var F = new Float32Array(n * 3), B = new Float32Array(n * 3);
        for (var k = 0; k < n; k++) {
            if (cB[k] > 1e-4 && (cF[k] > 1e-4 || gn)) {
                if (cF[k] > 1e-4) { F[k * 3] = FR[k] / cF[k]; F[k * 3 + 1] = FG[k] / cF[k]; F[k * 3 + 2] = FB[k] / cF[k]; }
                else { F[k * 3] = gF[0]; F[k * 3 + 1] = gF[1]; F[k * 3 + 2] = gF[2]; }
                B[k * 3] = BR[k] / cB[k]; B[k * 3 + 1] = BG[k] / cB[k]; B[k * 3 + 2] = BB[k] / cB[k];
            } else F[k * 3] = NaN;
        }
        return { x: bx0, y: by0, w: bw, h: bh, F: F, B: B };
    }

    // One layer on one pixel. acc = the pixel so far (linear, in place);
    // lin = its ORIGINAL linear colour; a = weight; ex, ey = the pixel's place
    // at the size the edge planes were made at.
    var F3 = [0, 0, 0], RF = [0, 0, 0];
    // → true when it changed the pixel. A pixel with weight 0 is still decided
    // here when it lies near the edge: a light-blue line between a white
    // stripe and a blue one turned red was left blue — the mix sat nearer the
    // white cluster (21.10.2026). Its projection says how much of it is object.
    function applyLayer(L, lin, a, acc, out, ex, ey, inside) {
        var P = L.P, s = L.strength == null ? 1 : L.strength, e = L.Wc && L.Wc.edge;
        // A zero-weight pixel is decided only INSIDE the object (a line between
        // two of its colours); outside, the mask's own edge is MaskEdge's call —
        // letting the projection in there painted the dark floor round a black
        // sofa (ring ΔE 25, 21.10.2026).
        if (e && a < EDGE_HI && (a > EDGE_LO || inside)) {
            var px = Math.round(ex) - e.x, py = Math.round(ey) - e.y;
            if (px >= 0 && py >= 0 && px < e.w && py < e.h) {
                var k = (py * e.w + px) * 3;
                if (e.F[k] === e.F[k]) {             // not NaN: both sides seen
                    var fr = e.F[k], fg = e.F[k + 1], fb = e.F[k + 2], br = e.B[k], bg = e.B[k + 1], bb = e.B[k + 2];
                    var dr = fr - br, dg = fg - bg, db = fb - bb, den = dr * dr + dg * dg + db * db;
                    if (den > 4e-4) {
                        var al = ((lin[0] - br) * dr + (lin[1] - bg) * dg + (lin[2] - bb) * db) / den;
                        al = al < 0 ? 0 : (al > 1 ? 1 : al);
                        // …only if the pixel IS on the line between the two: a
                        // skin-and-wall pixel on a person's outline projected
                        // onto «dress → skin» and drew a green contour round the
                        // figure (21.10.2026). Off the line → its weight decides.
                        var qr = lin[0] - (br + al * dr), qg = lin[1] - (bg + al * dg), qb = lin[2] - (bb + al * db);
                        if (qr * qr + qg * qg + qb * qb > EDGE_OFFLINE * den) al = -1;
                        if (al < 0) { /* not a mix of these two: fall through */ }
                        else if (al <= EDGE_LO) return a > EDGE_LO;
                        else {
                        F3[0] = fr; F3[1] = fg; F3[2] = fb;
                        recolour(P, F3, RF);
                        acc[0] += al * s * (RF[0] - fr); acc[1] += al * s * (RF[1] - fg); acc[2] += al * s * (RF[2] - fb);
                        if (acc[0] < 0) acc[0] = 0; if (acc[1] < 0) acc[1] = 0; if (acc[2] < 0) acc[2] = 0;
                        return true;
                        }
                    }
                }
            }
        }
        if (a <= 0.004) return false;
        recolour(P, lin, out);
        var t = a * s;
        acc[0] += (out[0] - acc[0]) * t; acc[1] += (out[1] - acc[1]) * t; acc[2] += (out[2] - acc[2]) * t;
        return true;
    }
    function recolour(P, lin, out) {
        if (P.mode === 'relight') return C.relightPixel(lin[0], lin[1], lin[2], P.R, out);
        if (P.mode === 'tint') return C.tintPixel(lin[0], lin[1], lin[2], P.R, out);
        return C.lutPixel(lin[0], lin[1], lin[2], P.R, out);
    }

    function paint(src, dst, w, h, layers, rows) {
        var y0 = rows ? rows[0] : 0, y1 = rows ? rows[1] : h;
        var lin = [0, 0, 0], out = [0, 0, 0], f = [0, 0, 0], acc = [0, 0, 0];
        if (!rows && src !== dst) dst.set(src);
        // With cached weights every layer knows its box; rows outside all boxes are a copy.
        var allCached = layers.every(function (L) { return L.Wc && L.Wc.w === w && L.Wc.h === h; });
        var bx0 = 0, bx1 = w - 1;
        if (allCached && layers.length) {
            var by0 = h, by1 = -1; bx0 = w; bx1 = -1;
            layers.forEach(function (L) {
                var c = L.Wc, e = c.edge;
                if (c.x1 < 0) return;
                by0 = Math.min(by0, e ? e.y : c.y0); by1 = Math.max(by1, e ? e.y + e.h - 1 : c.y1);
                bx0 = Math.min(bx0, e ? e.x : c.x0); bx1 = Math.max(bx1, e ? e.x + e.w - 1 : c.x1);
            });
            if (rows && src !== dst) for (var yc = y0; yc < y1; yc++) { var s0 = yc * w * 4; dst.set(src.subarray(s0, s0 + w * 4), s0); }
            y0 = Math.max(y0, by0); y1 = Math.min(y1, by1 + 1);
        } else if (rows && src !== dst) {
            for (var yr = y0; yr < y1; yr++) { var s1 = yr * w * 4; dst.set(src.subarray(s1, s1 + w * 4), s1); }
        }
        for (var y = y0; y < y1; y++) {
            for (var x = bx0; x <= bx1; x++) {
                var i = (y * w + x) * 4;
                var r = src[i], g = src[i + 1], b = src[i + 2];
                lin[0] = LIN[r]; lin[1] = LIN[g]; lin[2] = LIN[b];
                acc[0] = lin[0]; acc[1] = lin[1]; acc[2] = lin[2];
                var touched = false;
                for (var l = 0; l < layers.length; l++) {
                    var L = layers[l], an = L.an, a, ex = x, ey = y, inside;
                    if (L.Wc && L.Wc.w === w && L.Wc.h === h) {
                        a = L.Wc.W[y * w + x]; inside = L.Wc.IN[y * w + x] === 1;
                        if (a <= 0.004 && !(inside && L.Wc.edge)) continue;
                    } else {
                        var ax = (x + 0.5) * an.w / w - 0.5, ay = (y + 0.5) * an.h / h - 0.5;
                        var a2 = sample(an.alpha, an.w, an.h, ax, ay);
                        inside = a2 > 0.004;
                        a = inside ? weightAt(an, r, g, b, a2, sample(an.Lsm, an.w, an.h, ax, ay), L.keepPattern, f) : 0;
                        if (L.Wc) { ex = (x + 0.5) * L.Wc.w / w - 0.5; ey = (y + 0.5) * L.Wc.h / h - 0.5; }
                        if (a <= 0.004 && !(inside && L.Wc && L.Wc.edge)) continue;
                    }
                    if (applyLayer(L, lin, a, acc, out, ex, ey, inside)) touched = true;
                }
                if (touched) {
                    dst[i] = C.encodeByte(acc[0]); dst[i + 1] = C.encodeByte(acc[1]); dst[i + 2] = C.encodeByte(acc[2]);
                } else { dst[i] = r; dst[i + 1] = g; dst[i + 2] = b; }
                dst[i + 3] = src[i + 3];
            }
        }
        return dst;
    }

    /* A STRIP of a picture of w×h (rows y0..y1-1), src/dst holding just those
       rows — the export paints a 24-MP photo this way without a second full
       copy of it in memory. Same arithmetic as paint(): the weight is decided
       at THIS size, the edge planes (made at the working size) are read at the
       pixel's place there. */
    function paintRows(src, dst, w, h, y0, y1, layers) {
        var lin = [0, 0, 0], out = [0, 0, 0], f = [0, 0, 0], acc = [0, 0, 0];
        for (var y = y0; y < y1; y++) {
            for (var x = 0; x < w; x++) {
                var i = ((y - y0) * w + x) * 4;
                var r = src[i], g = src[i + 1], b = src[i + 2];
                lin[0] = LIN[r]; lin[1] = LIN[g]; lin[2] = LIN[b];
                acc[0] = lin[0]; acc[1] = lin[1]; acc[2] = lin[2];
                var touched = false;
                for (var l = 0; l < layers.length; l++) {
                    var L = layers[l], an = L.an;
                    var ax = (x + 0.5) * an.w / w - 0.5, ay = (y + 0.5) * an.h / h - 0.5;
                    var a3 = sample(an.alpha, an.w, an.h, ax, ay);
                    if (a3 <= 0.004) continue;
                    var a = weightAt(an, r, g, b, a3, sample(an.Lsm, an.w, an.h, ax, ay), L.keepPattern, f);
                    if (a <= 0.004 && !(L.Wc && L.Wc.edge)) continue;
                    var ex = L.Wc ? (x + 0.5) * L.Wc.w / w - 0.5 : x, ey = L.Wc ? (y + 0.5) * L.Wc.h / h - 0.5 : y;
                    if (applyLayer(L, lin, a, acc, out, ex, ey, true)) touched = true;
                }
                if (touched) { dst[i] = C.encodeByte(acc[0]); dst[i + 1] = C.encodeByte(acc[1]); dst[i + 2] = C.encodeByte(acc[2]); }
                else { dst[i] = r; dst[i + 1] = g; dst[i + 2] = b; }
                dst[i + 3] = src[i + 3];
            }
        }
        return dst;
    }

    return {
        paintRows: paintRows,
        boxBlur: boxBlur, guided: guided, luminance: luminance,
        analyze: analyze, patternWeightAt: patternWeightAt, weightAt: weightAt, referenceOf: referenceOf,
        prepare: prepare, tintCarries: tintCarries, paint: paint, weights: weights, sample: sample,
        PAT_K: PAT_K, PAT_MERGE: PAT_MERGE,
    };
}));
